import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useInviteAuth } from './InviteAuthContext';
import { useInviteTheme } from './InviteThemeContext';
import API_BASE_URL from '../../config';
import {
    CheckCircle2,
    AlertCircle,
    RefreshCw,
    Smartphone,
    LogOut,
    ArrowLeft,
    QrCode,
    Phone,
    Wifi,
    WifiOff,
    Loader2,
    Shield,
    Check,
    Sparkles,
} from 'lucide-react';
import { Link, useLocation, useHistory } from 'react-router-dom';

// ─── Types ───────────────────────────────────────────────────────────────────

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';
type LinkMethod = 'mobile' | 'phone' | 'qr';

interface SessionStatus {
    status: ConnectionStatus;
    phoneNumber: string | null;
    qrCode: string | null;         // base64 image from Evolution API (may be data URI or raw)
    pairingCode: string | null;    // 8-digit code for phone-number pairing
    isOnline: boolean;
    instanceName?: string;
}

// ─── Helpers: Mobile & Platform Detection ───────────────────────────────────
function isMobileDevice(): boolean {
    if (typeof window === 'undefined') return false;
    const isMobileWidth = window.innerWidth <= 768;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    return isMobileWidth || isMobileUA;
}

function getInitialPlatform(): 'android' | 'ios' {
    if (typeof window !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        return 'ios';
    }
    return 'android';
}

// ─── Helper: normalise QR source for <img> ───────────────────────────────────
function buildQrSrc(raw: string): string {
    if (!raw) return '';
    if (raw.startsWith('data:')) return raw;
    if (!raw.startsWith('http')) return `data:image/png;base64,${raw}`;
    return raw;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export const InviteWhatsAppSettings: React.FC = () => {
    const { inviteToken } = useInviteAuth();
    const { theme } = useInviteTheme();
    const isLightTheme = theme === 'light';

    const location = useLocation();
    const history = useHistory();
    const query = new URLSearchParams(location.search);
    const returnTo = query.get('returnTo');
    const returnStep = query.get('step') || '1';
    const effectiveReturnUrl = returnTo ? `${returnTo}?step=${returnStep}&returnFrom=whatsapp` : '/invite/dashboard';

    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [pairingLoading, setPairingLoading] = useState(false);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [refreshSuccess, setRefreshSuccess] = useState(false);

    const [statusData, setStatusData] = useState<SessionStatus>({
        status: 'disconnected',
        phoneNumber: null,
        qrCode: null,
        pairingCode: null,
        isOnline: false,
    });

    const [linkMethod, setLinkMethod] = useState<LinkMethod>(() => (isMobileDevice() ? 'mobile' : 'qr'));
    const [mobilePlatform, setMobilePlatform] = useState<'android' | 'ios'>(() => getInitialPlatform());
    const [copiedCode, setCopiedCode] = useState(false);
    const [phoneInput, setPhoneInput] = useState('');
    const [pairingCode, setPairingCode] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [instanceOnline, setInstanceOnline] = useState<boolean | null>(null);

    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const handleCopyCode = (code: string) => {
        if (!code) return;
        navigator.clipboard?.writeText(code);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2200);
    };

    // ─── API Calls ──────────────────────────────────────────────────────────

    const authHeaders = useCallback(() => ({
        Authorization: `Bearer ${inviteToken}`,
        'Content-Type': 'application/json',
    }), [inviteToken]);

    // Check Evolution API instance health
    const fetchInstanceHealth = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/invite/whatsapp/instance/health`, {
                headers: authHeaders(),
            });
            const data = await res.json();
            setInstanceOnline(data?.isOnline ?? false);
        } catch {
            setInstanceOnline(false);
        }
    }, [authHeaders]);

    // Poll session status from backend
    const fetchStatus = useCallback(async (showLoading = false) => {
        if (!inviteToken) return;
        if (showLoading) setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/invite/whatsapp/session/status`, {
                headers: authHeaders(),
            });

            if (!res.ok) throw new Error('Failed to retrieve WhatsApp status');

            const data: SessionStatus = await res.json();
            setStatusData(data);

            // If we got a pairing code from the status poll, surface it
            if (data.pairingCode && !pairingCode) {
                setPairingCode(data.pairingCode);
            }

            // Stop polling once connected
            if (data.status === 'connected' && pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
                setPairingCode(null);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [inviteToken, authHeaders, pairingCode]);

    // ─── Polling logic ───────────────────────────────────────────────────────

    useEffect(() => {
        if (statusData.status === 'connecting') {
            if (!pollingIntervalRef.current) {
                pollingIntervalRef.current = setInterval(() => fetchStatus(false), 2500);
            }
        } else {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        }

        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [statusData.status, fetchStatus]);

    // Initial load
    useEffect(() => {
        if (inviteToken) {
            fetchStatus(true);
            fetchInstanceHealth();
        }
    }, [inviteToken, fetchStatus, fetchInstanceHealth]);

    // ─── Actions ─────────────────────────────────────────────────────────────

    // Manual Refresh with feedback
    const handleManualRefresh = async () => {
        setRefreshLoading(true);
        setError(null);
        try {
            await fetchStatus(false);
            await fetchInstanceHealth();
            setRefreshSuccess(true);
            setTimeout(() => setRefreshSuccess(false), 2500);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setRefreshLoading(false);
        }
    };

    // Connect via QR
    const handleConnectQr = async () => {
        setActionLoading(true);
        setError(null);
        setPairingCode(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/invite/whatsapp/session/initiate`, {
                method: 'POST',
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error('Failed to initiate connection.');
            setStatusData(prev => ({ ...prev, status: 'connecting', qrCode: null }));
            setTimeout(() => fetchStatus(false), 1000);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setActionLoading(false);
        }
    };

    // Request phone pairing code
    const handleRequestPairingCode = async () => {
        if (!phoneInput.trim()) {
            setError('Please enter your phone number with country code (e.g. 919876543210)');
            return;
        }
        setPairingLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/invite/whatsapp/session/pairing-code`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ phone: phoneInput.trim() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Failed to get pairing code');
            setPairingCode(data.pairingCode);
            setStatusData(prev => ({ ...prev, status: 'connecting' }));
        } catch (err: any) {
            setError(err.message);
        } finally {
            setPairingLoading(false);
        }
    };

    // Disconnect
    const handleDisconnect = async () => {
        if (!window.confirm('Are you sure you want to disconnect? This will log out your WhatsApp session.')) return;
        setActionLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/invite/whatsapp/session/disconnect`, {
                method: 'POST',
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error('Failed to disconnect session.');
            setPairingCode(null);
            setStatusData(prev => ({ ...prev, status: 'disconnected', qrCode: null, phoneNumber: null, isOnline: false }));
            await fetchStatus(false);
            fetchInstanceHealth();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setActionLoading(false);
        }
    };

    // Reconnect / Relink (Force Disconnect + Initiate fresh QR session)
    const handleReconnect = async () => {
        setActionLoading(true);
        setError(null);
        setPairingCode(null);
        try {
            // 1. Reset old session on Evolution API via MsgService
            await fetch(`${API_BASE_URL}/api/invite/whatsapp/session/disconnect`, {
                method: 'POST',
                headers: authHeaders(),
            }).catch(() => {});

            // 2. Initiate fresh session
            const res = await fetch(`${API_BASE_URL}/api/invite/whatsapp/session/initiate`, {
                method: 'POST',
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error('Failed to start new WhatsApp connection session.');

            setStatusData(prev => ({ ...prev, status: 'connecting', qrCode: null, pairingCode: null, isOnline: false }));
            // Poll right away and after short delay
            setTimeout(() => fetchStatus(false), 800);
            setTimeout(() => fetchStatus(false), 2000);
            fetchInstanceHealth();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setActionLoading(false);
        }
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '1rem', color: '#C084FC' }}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
                <p style={{ fontWeight: 600, margin: 0, color: '#94A3B8' }}>Loading WhatsApp Settings...</p>
            </div>
        );
    }

    const qrSrc = statusData.qrCode ? buildQrSrc(statusData.qrCode) : null;

    return (
        <div style={{ maxWidth: '860px', margin: '0 auto', padding: '1rem 0', position: 'relative', zIndex: 1 }}>

            {/* ── Return-to-Wizard Banner (if user navigated from Wizard) ────── */}
            {returnTo && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.9rem 1.25rem',
                    borderRadius: '16px',
                    background: statusData.status === 'connected' 
                        ? (isLightTheme ? '#ECFDF5' : 'rgba(16, 185, 129, 0.12)') 
                        : (isLightTheme ? '#FEF3C7' : 'rgba(245, 158, 11, 0.12)'),
                    border: `1.5px solid ${statusData.status === 'connected' ? '#10B981' : '#F59E0B'}`,
                    marginBottom: '1.5rem',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                    boxShadow: statusData.status === 'connected' ? '0 8px 24px rgba(16, 185, 129, 0.15)' : 'none'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '1.5rem' }}>{statusData.status === 'connected' ? '🎉' : '📝'}</span>
                        <div>
                            <div style={{ fontWeight: 800, fontSize: '0.94rem', color: statusData.status === 'connected' ? '#059669' : '#D97706' }}>
                                {statusData.status === 'connected' 
                                    ? 'WhatsApp Connected! You are ready to broadcast.' 
                                    : 'Linking WhatsApp for your Invitation'}
                            </div>
                            <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: isLightTheme ? '#475569' : '#94A3B8' }}>
                                {statusData.status === 'connected'
                                    ? 'Your account is linked. Return to the wizard to continue designing & dispatching.'
                                    : 'Your invitation draft and guest list are safely saved. Link your WhatsApp below.'}
                            </p>
                        </div>
                    </div>
                    <Link
                        to={effectiveReturnUrl}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '9px 18px',
                            borderRadius: '12px',
                            background: statusData.status === 'connected'
                                ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                                : 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                            color: '#FFFFFF',
                            fontWeight: 700,
                            fontSize: '0.88rem',
                            textDecoration: 'none',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
                        }}
                    >
                        <span>Return to Invitation Wizard ➡️</span>
                    </Link>
                </div>
            )}

            {/* ── Page Header ───────────────────────────────────────────── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
                <Link
                    to={effectiveReturnUrl}
                    title={returnTo ? "Return to Invitation Wizard" : "Back to Dashboard"}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        border: isLightTheme ? '1px solid #CBD5E1' : '1px solid rgba(255,255,255,0.12)',
                        background: isLightTheme ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
                        color: isLightTheme ? '#7C3AED' : '#C084FC',
                        textDecoration: 'none',
                        flexShrink: 0,
                        boxShadow: isLightTheme ? '0 2px 6px rgba(0,0,0,0.03)' : 'none',
                    }}
                >
                    <ArrowLeft size={18} />
                </Link>
                <div style={{ flex: 1 }}>
                    <h2 style={{ fontSize: '1.75rem', fontWeight: 800, margin: 0, fontFamily: "'Outfit', sans-serif", color: isLightTheme ? '#0F172A' : '#FFFFFF', letterSpacing: '-0.5px' }}>
                        WhatsApp Connection
                    </h2>
                    <p style={{ color: isLightTheme ? '#64748B' : '#94A3B8', fontSize: '0.88rem', margin: '0.2rem 0 0' }}>
                        Powered by Evolution API &amp; WhastFlow — broadcast invitations directly from your personal number.
                    </p>
                </div>

                {/* Instance health badge + Refresh Indicator */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {refreshSuccess && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.78rem', color: isLightTheme ? '#059669' : '#34D399', fontWeight: 700, background: isLightTheme ? '#ECFDF5' : 'rgba(16,185,129,0.15)', border: `1px solid ${isLightTheme ? '#A7F3D0' : 'rgba(16,185,129,0.3)'}`, padding: '0.3rem 0.6rem', borderRadius: '12px' }}>
                            <Check size={12} /> Updated!
                        </span>
                    )}
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                        padding: '0.35rem 0.85rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 700,
                        background: instanceOnline 
                            ? (isLightTheme ? '#ECFDF5' : 'rgba(16,185,129,0.12)') 
                            : (isLightTheme ? '#FEF2F2' : 'rgba(239,68,68,0.12)'),
                        border: `1px solid ${instanceOnline 
                            ? (isLightTheme ? '#A7F3D0' : 'rgba(16,185,129,0.3)') 
                            : (isLightTheme ? '#FECACA' : 'rgba(239,68,68,0.3)')}`,
                        color: instanceOnline 
                            ? (isLightTheme ? '#059669' : '#34D399') 
                            : (isLightTheme ? '#DC2626' : '#F87171'),
                    }}>
                        {instanceOnline
                            ? <><Wifi size={13} /> API Online</>
                            : <><WifiOff size={13} /> API Offline</>
                        }
                    </div>
                </div>
            </div>

            {/* ── Error Banner ───────────────────────────────────────────── */}
            {error && (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', padding: '1rem 1.25rem', background: isLightTheme ? '#FEF2F2' : 'rgba(239,68,68,0.15)', border: `1px solid ${isLightTheme ? '#FECACA' : 'rgba(239,68,68,0.35)'}`, borderRadius: '14px', color: isLightTheme ? '#DC2626' : '#FCA5A5', marginBottom: '1.5rem', fontSize: '0.88rem', fontWeight: 600 }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <AlertCircle size={20} style={{ flexShrink: 0 }} />
                        <span>{error}</span>
                    </div>
                    <button
                        onClick={handleReconnect}
                        disabled={actionLoading}
                        style={{ background: isLightTheme ? '#FFFFFF' : 'transparent', border: `1px solid ${isLightTheme ? '#FECACA' : 'rgba(239,68,68,0.4)'}`, borderRadius: '8px', padding: '4px 10px', color: isLightTheme ? '#DC2626' : '#FCA5A5', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                    >
                        <RefreshCw size={12} /> Retry Reconnect
                    </button>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                STATUS: CONNECTED
            ══════════════════════════════════════════════════════════════ */}
            {statusData.status === 'connected' && (
                <div style={{ background: isLightTheme ? '#FFFFFF' : 'rgba(17, 24, 39, 0.75)', border: `1px solid ${isLightTheme ? '#A7F3D0' : 'rgba(16,185,129,0.35)'}`, borderRadius: '20px', padding: '2.5rem', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', boxShadow: isLightTheme ? '0 10px 30px rgba(0,0,0,0.04)' : '0 12px 36px rgba(0,0,0,0.35)', textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '72px', height: '72px', borderRadius: '50%', background: isLightTheme ? '#ECFDF5' : 'rgba(16,185,129,0.15)', color: isLightTheme ? '#059669' : '#34D399', marginBottom: '1.25rem', border: `1px solid ${isLightTheme ? '#A7F3D0' : 'rgba(16,185,129,0.3)'}` }}>
                        <CheckCircle2 size={36} />
                    </div>
                    <h3 style={{ fontSize: '1.5rem', color: isLightTheme ? '#0F172A' : '#FFFFFF', margin: '0 0 0.5rem 0', fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>
                        WhatsApp Linked! 🎉
                    </h3>
                    <p style={{ color: isLightTheme ? '#64748B' : '#94A3B8', fontSize: '0.92rem', margin: '0 0 0.4rem 0' }}>
                        Connected via <strong>Evolution API</strong> · Instance: <code style={{ background: isLightTheme ? '#F1F5F9' : 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: '6px', fontSize: '0.85rem', color: isLightTheme ? '#7C3AED' : '#C084FC' }}>{statusData.instanceName || 'Isolated Instance'}</code>
                    </p>
                    {statusData.phoneNumber && (
                        <p style={{ color: isLightTheme ? '#059669' : '#34D399', fontWeight: 700, fontSize: '1.1rem', margin: '0 0 2rem 0' }}>
                            📱 +{statusData.phoneNumber}
                        </p>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
                        <button
                            id="btn-whatsapp-refresh"
                            onClick={handleManualRefresh}
                            disabled={refreshLoading}
                            style={{ cursor: refreshLoading ? 'not-allowed' : 'pointer', background: isLightTheme ? '#FFFFFF' : 'rgba(255,255,255,0.05)', border: `1px solid ${isLightTheme ? '#CBD5E1' : 'rgba(255,255,255,0.12)'}`, borderRadius: '12px', padding: '10px 22px', color: isLightTheme ? '#334155' : '#CBD5E1', fontSize: '0.85rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.2s', opacity: refreshLoading ? 0.6 : 1, boxShadow: isLightTheme ? '0 2px 6px rgba(0,0,0,0.02)' : 'none' }}
                        >
                            <RefreshCw size={14} style={{ animation: refreshLoading ? 'spin 1s linear infinite' : 'none' }} />
                            {refreshLoading ? 'Checking...' : 'Refresh Status'}
                        </button>
                        <button
                            id="btn-whatsapp-reconnect"
                            onClick={handleReconnect}
                            disabled={actionLoading}
                            style={{
                                cursor: actionLoading ? 'not-allowed' : 'pointer',
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                border: 'none', borderRadius: '12px', padding: '10px 24px',
                                color: '#FFF', fontSize: '0.85rem', fontWeight: 800,
                                display: 'flex', alignItems: 'center', gap: '0.5rem',
                                boxShadow: '0 4px 15px rgba(139,92,246,0.35)',
                                opacity: actionLoading ? 0.6 : 1, transition: 'all 0.2s',
                            }}
                        >
                            {actionLoading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={14} />}
                            {actionLoading ? 'Reconnecting...' : '🔄 Reconnect / Relink Device'}
                        </button>
                        <button
                            id="btn-whatsapp-disconnect"
                            onClick={handleDisconnect}
                            disabled={actionLoading}
                            style={{ cursor: 'pointer', background: isLightTheme ? '#FEF2F2' : 'rgba(239,68,68,0.12)', border: `1px solid ${isLightTheme ? '#FECACA' : 'rgba(239,68,68,0.3)'}`, borderRadius: '12px', padding: '10px 22px', color: isLightTheme ? '#DC2626' : '#FCA5A5', fontSize: '0.85rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem', transition: 'all 0.2s', opacity: actionLoading ? 0.6 : 1 }}
                        >
                            <LogOut size={14} /> Disconnect
                        </button>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                STATUS: DISCONNECTED — Choose connection method
            ══════════════════════════════════════════════════════════════ */}
            {statusData.status === 'disconnected' && (
                <div style={{ background: isLightTheme ? '#FFFFFF' : 'rgba(17, 24, 39, 0.75)', border: `1px solid ${isLightTheme ? '#E2E8F0' : 'rgba(255, 255, 255, 0.08)'}`, borderRadius: '20px', padding: '2.5rem', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', boxShadow: isLightTheme ? '0 10px 30px rgba(0,0,0,0.04)' : '0 12px 36px rgba(0,0,0,0.35)' }}>
                    {/* Icon + heading */}
                    <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '72px', height: '72px', borderRadius: '50%', background: isLightTheme ? '#F1F5F9' : 'rgba(255,255,255,0.05)', color: isLightTheme ? '#64748B' : '#94A3B8', marginBottom: '1.25rem', border: `1px solid ${isLightTheme ? '#E2E8F0' : 'rgba(255,255,255,0.1)'}` }}>
                            <Smartphone size={32} />
                        </div>
                        <h3 style={{ fontSize: '1.4rem', color: isLightTheme ? '#0F172A' : '#FFFFFF', margin: '0 0 0.5rem 0', fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>
                            WhatsApp Not Connected
                        </h3>
                        <p style={{ color: isLightTheme ? '#64748B' : '#94A3B8', fontSize: '0.9rem', maxWidth: '460px', margin: '0 auto', lineHeight: 1.6 }}>
                            Link your WhatsApp via QR code or phone number pairing. Invitations will be dispatched from your personal number.
                        </p>
                    </div>

                    {/* Method Selector Tabs */}
                    <div style={{
                        display: 'flex',
                        borderRadius: '14px',
                        background: isLightTheme ? '#F1F5F9' : 'rgba(15, 23, 42, 0.8)',
                        border: `1px solid ${isLightTheme ? '#E2E8F0' : 'rgba(255, 255, 255, 0.08)'}`,
                        padding: '4px',
                        marginBottom: '2rem',
                        maxWidth: '520px',
                        margin: '0 auto 2rem auto',
                        gap: '4px',
                        flexWrap: 'wrap',
                    }}>
                        <button
                            id="whatsapp-mobile-tab"
                            onClick={() => { setLinkMethod('mobile'); setError(null); }}
                            style={{
                                flex: '1 1 150px',
                                padding: '9px 10px',
                                borderRadius: '10px',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '0.84rem',
                                fontWeight: 700,
                                transition: 'all 0.2s',
                                background: linkMethod === 'mobile' ? 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)' : 'transparent',
                                color: linkMethod === 'mobile' ? '#FFFFFF' : (isLightTheme ? '#475569' : '#94A3B8'),
                                boxShadow: linkMethod === 'mobile' ? '0 4px 12px rgba(139,92,246,0.3)' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.4rem',
                            }}
                        >
                            <Smartphone size={15} /> 📱 Mobile Flow
                            {isMobileDevice() && (
                                <span style={{
                                    fontSize: '0.65rem',
                                    background: linkMethod === 'mobile' ? '#10B981' : (isLightTheme ? '#E2E8F0' : 'rgba(255,255,255,0.15)'),
                                    color: linkMethod === 'mobile' ? '#FFFFFF' : (isLightTheme ? '#0F172A' : '#F8FAFC'),
                                    padding: '1px 5px',
                                    borderRadius: '6px',
                                    fontWeight: 800,
                                }}>
                                    Default
                                </span>
                            )}
                        </button>
                        <button
                            id="whatsapp-phone-tab"
                            onClick={() => { setLinkMethod('phone'); setError(null); }}
                            style={{
                                flex: '1 1 120px',
                                padding: '9px 10px',
                                borderRadius: '10px',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '0.84rem',
                                fontWeight: 700,
                                transition: 'all 0.2s',
                                background: linkMethod === 'phone' ? 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)' : 'transparent',
                                color: linkMethod === 'phone' ? '#FFFFFF' : (isLightTheme ? '#475569' : '#94A3B8'),
                                boxShadow: linkMethod === 'phone' ? '0 4px 12px rgba(139,92,246,0.3)' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.4rem',
                            }}
                        >
                            <Phone size={15} /> Direct Code
                        </button>
                        <button
                            id="whatsapp-qr-tab"
                            onClick={() => { setLinkMethod('qr'); setError(null); }}
                            style={{
                                flex: '1 1 120px',
                                padding: '9px 10px',
                                borderRadius: '10px',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '0.84rem',
                                fontWeight: 700,
                                transition: 'all 0.2s',
                                background: linkMethod === 'qr' ? 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)' : 'transparent',
                                color: linkMethod === 'qr' ? '#FFFFFF' : (isLightTheme ? '#475569' : '#94A3B8'),
                                boxShadow: linkMethod === 'qr' ? '0 4px 12px rgba(139,92,246,0.3)' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '0.4rem',
                            }}
                        >
                            <QrCode size={15} /> QR Code (PC)
                        </button>
                    </div>

                    {/* ── 1. Mobile Flow Method ─────────────────────────────── */}
                    {linkMethod === 'mobile' && (
                        <div style={{ maxWidth: '620px', margin: '0 auto', textAlign: 'left' }}>
                            {/* Context banner */}
                            <div style={{
                                background: isLightTheme ? '#F5F3FF' : 'rgba(139, 92, 246, 0.1)',
                                border: `1px solid ${isLightTheme ? '#DDD6FE' : 'rgba(139, 92, 246, 0.25)'}`,
                                borderRadius: '14px',
                                padding: '0.9rem 1.1rem',
                                marginBottom: '1.5rem',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.75rem',
                            }}>
                                <Sparkles size={18} style={{ color: isLightTheme ? '#7C3AED' : '#C084FC', flexShrink: 0, marginTop: '2px' }} />
                                <div style={{ fontSize: '0.84rem', color: isLightTheme ? '#4C1D95' : '#DDD6FE', lineHeight: 1.5 }}>
                                    <strong>Linking directly on this phone?</strong> You cannot scan a QR code on the same screen. Follow this 3-step phone pairing flow to link Invite Studio to your WhatsApp seamlessly.
                                    <span style={{ display: 'block', marginTop: '4px', fontSize: '0.78rem', color: isLightTheme ? '#6D28D9' : '#C4B5FD' }}>
                                        ✓ Your current WhatsApp chats &amp; app will remain completely untouched.
                                    </span>
                                </div>
                            </div>

                            {/* Step 1: Input & Code Generation */}
                            <div style={{
                                background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.6)',
                                border: `1px solid ${isLightTheme ? '#E2E8F0' : 'rgba(255, 255, 255, 0.08)'}`,
                                borderRadius: '14px',
                                padding: '1.25rem',
                                marginBottom: '1rem',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.5rem' }}>
                                    <span style={{
                                        width: '26px', height: '26px', borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                        color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 800, fontSize: '0.82rem', flexShrink: 0,
                                    }}>1</span>
                                    <span style={{ fontWeight: 800, fontSize: '0.96rem', color: isLightTheme ? '#0F172A' : '#FFFFFF' }}>
                                        Enter your WhatsApp Phone Number
                                    </span>
                                </div>
                                <p style={{ fontSize: '0.82rem', color: isLightTheme ? '#64748B' : '#94A3B8', margin: '0 0 0.85rem 2.2rem', lineHeight: 1.4 }}>
                                    Include country code without '+' (e.g. <code>919876543210</code> for India).
                                </p>
                                <div style={{ display: 'flex', gap: '0.6rem', marginLeft: '2.2rem', flexWrap: 'wrap' }}>
                                    <input
                                        id="mobile-flow-phone-input"
                                        type="tel"
                                        value={phoneInput}
                                        onChange={e => setPhoneInput(e.target.value)}
                                        placeholder="e.g. 919876543210"
                                        style={{
                                            flex: '1 1 200px',
                                            padding: '10px 14px',
                                            borderRadius: '10px',
                                            border: `1.5px solid ${isLightTheme ? '#CBD5E1' : 'rgba(255,255,255,0.12)'}`,
                                            fontSize: '0.92rem',
                                            outline: 'none',
                                            background: isLightTheme ? '#FFFFFF' : 'rgba(15,23,42,0.9)',
                                            color: isLightTheme ? '#0F172A' : '#F8FAFC',
                                            fontFamily: 'monospace',
                                            letterSpacing: '1px',
                                        }}
                                        onKeyDown={e => e.key === 'Enter' && handleRequestPairingCode()}
                                    />
                                    <button
                                        id="btn-mobile-flow-get-code"
                                        onClick={handleRequestPairingCode}
                                        disabled={pairingLoading || !instanceOnline}
                                        style={{
                                            cursor: pairingLoading || !instanceOnline ? 'not-allowed' : 'pointer',
                                            border: 'none',
                                            borderRadius: '10px',
                                            padding: '10px 20px',
                                            background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                            color: '#FFF',
                                            fontWeight: 700,
                                            fontSize: '0.86rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.4rem',
                                            opacity: pairingLoading || !instanceOnline ? 0.6 : 1,
                                            whiteSpace: 'nowrap',
                                            boxShadow: '0 4px 12px rgba(139,92,246,0.3)',
                                        }}
                                    >
                                        {pairingLoading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Shield size={14} />}
                                        {pairingLoading ? 'Generating...' : 'Get 8-Digit Code'}
                                    </button>
                                </div>
                            </div>

                            {/* Step 2: Open WhatsApp & Instructions with OS Switcher */}
                            <div style={{
                                background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.6)',
                                border: `1px solid ${isLightTheme ? '#E2E8F0' : 'rgba(255, 255, 255, 0.08)'}`,
                                borderRadius: '14px',
                                padding: '1.25rem',
                                marginBottom: '1rem',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                                        <span style={{
                                            width: '26px', height: '26px', borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                            color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontWeight: 800, fontSize: '0.82rem', flexShrink: 0,
                                        }}>2</span>
                                        <span style={{ fontWeight: 800, fontSize: '0.96rem', color: isLightTheme ? '#0F172A' : '#FFFFFF' }}>
                                            Open WhatsApp on this Phone
                                        </span>
                                    </div>

                                    {/* OS Toggle */}
                                    <div style={{ display: 'inline-flex', background: isLightTheme ? '#E2E8F0' : 'rgba(255,255,255,0.08)', borderRadius: '8px', padding: '2px' }}>
                                        <button
                                            type="button"
                                            onClick={() => setMobilePlatform('android')}
                                            style={{
                                                padding: '4px 10px',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                background: mobilePlatform === 'android' ? (isLightTheme ? '#FFFFFF' : '#8B5CF6') : 'transparent',
                                                color: mobilePlatform === 'android' ? (isLightTheme ? '#0F172A' : '#FFFFFF') : (isLightTheme ? '#64748B' : '#94A3B8'),
                                                boxShadow: mobilePlatform === 'android' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                            }}
                                        >
                                            🤖 Android
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setMobilePlatform('ios')}
                                            style={{
                                                padding: '4px 10px',
                                                border: 'none',
                                                borderRadius: '6px',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                background: mobilePlatform === 'ios' ? (isLightTheme ? '#FFFFFF' : '#8B5CF6') : 'transparent',
                                                color: mobilePlatform === 'ios' ? (isLightTheme ? '#0F172A' : '#FFFFFF') : (isLightTheme ? '#64748B' : '#94A3B8'),
                                                boxShadow: mobilePlatform === 'ios' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                            }}
                                        >
                                            🍎 iPhone
                                        </button>
                                    </div>
                                </div>

                                {/* Platform specific list */}
                                <div style={{ marginLeft: '2.2rem' }}>
                                    {mobilePlatform === 'android' ? (
                                        <ol style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.84rem', color: isLightTheme ? '#334155' : '#CBD5E1', lineHeight: 1.85 }}>
                                            <li>Open the <strong>WhatsApp</strong> app on this device.</li>
                                            <li>Tap the <strong>three vertical dots (⋮)</strong> in the top-right corner.</li>
                                            <li>Select <strong>Linked Devices</strong>.</li>
                                            <li>Tap <strong>Link a Device</strong> (unlock your screen if requested).</li>
                                            <li>At the bottom of the camera scanner screen, tap <strong style={{ color: '#8B5CF6' }}>"Link with phone number instead"</strong>.</li>
                                        </ol>
                                    ) : (
                                        <ol style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.84rem', color: isLightTheme ? '#334155' : '#CBD5E1', lineHeight: 1.85 }}>
                                            <li>Open the <strong>WhatsApp</strong> app on this iPhone.</li>
                                            <li>Tap <strong>Settings (⚙️)</strong> in the bottom-right corner.</li>
                                            <li>Select <strong>Linked Devices</strong>.</li>
                                            <li>Tap <strong>Link a Device</strong> (unlock with Face ID or Passcode).</li>
                                            <li>At the bottom of the camera scanner, tap <strong style={{ color: '#8B5CF6' }}>"Link with phone number instead"</strong>.</li>
                                        </ol>
                                    )}
                                </div>
                            </div>

                            {/* Step 3: Enter code and finish */}
                            <div style={{
                                background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.6)',
                                border: `1px solid ${isLightTheme ? '#E2E8F0' : 'rgba(255, 255, 255, 0.08)'}`,
                                borderRadius: '14px',
                                padding: '1.25rem',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.5rem' }}>
                                    <span style={{
                                        width: '26px', height: '26px', borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                        color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 800, fontSize: '0.82rem', flexShrink: 0,
                                    }}>3</span>
                                    <span style={{ fontWeight: 800, fontSize: '0.96rem', color: isLightTheme ? '#0F172A' : '#FFFFFF' }}>
                                        Type the 8-Digit Code &amp; Done!
                                    </span>
                                </div>
                                <p style={{ fontSize: '0.83rem', color: isLightTheme ? '#64748B' : '#94A3B8', margin: '0 0 0 2.2rem', lineHeight: 1.5 }}>
                                    Enter the 8-digit code on WhatsApp. Once entered, switch back to this tab — the status will turn into <strong style={{ color: isLightTheme ? '#059669' : '#34D399' }}>"WhatsApp Linked! 🎉"</strong> and your invites will broadcast from your own number!
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ── 2. QR Method ──────────────────────────────────────── */}
                    {linkMethod === 'qr' && (
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ color: isLightTheme ? '#475569' : '#94A3B8', fontSize: '0.88rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
                                Click below to generate a QR code. Then open <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>WhatsApp → Linked Devices → Link a Device</strong> and scan it.
                            </p>
                            <button
                                id="btn-generate-qr"
                                onClick={handleConnectQr}
                                disabled={actionLoading || !instanceOnline}
                                style={{
                                    cursor: actionLoading || !instanceOnline ? 'not-allowed' : 'pointer',
                                    border: 'none', borderRadius: '12px', padding: '12px 32px',
                                    background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                    color: '#FFF', fontWeight: 800, fontSize: '0.92rem',
                                    display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
                                    boxShadow: '0 4px 16px rgba(139,92,246,0.35)',
                                    opacity: actionLoading || !instanceOnline ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                }}
                            >
                                {actionLoading
                                    ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Generating QR Code...</>
                                    : <><QrCode size={16} /> Generate QR Code</>
                                }
                            </button>
                            {!instanceOnline && (
                                <p style={{ color: '#DC2626', fontSize: '0.8rem', marginTop: '0.75rem', fontWeight: 600 }}>
                                    ⚠️ Evolution API is offline. Please check the WhastFlow deployment.
                                </p>
                            )}
                        </div>
                    )}

                    {/* ── 3. Direct Phone Pairing Method ────────────────────── */}
                    {linkMethod === 'phone' && (
                        <div style={{ maxWidth: '400px', margin: '0 auto', textAlign: 'left' }}>
                            <p style={{ color: isLightTheme ? '#475569' : '#94A3B8', fontSize: '0.88rem', marginBottom: '1.25rem', lineHeight: 1.5, textAlign: 'center' }}>
                                Enter your WhatsApp phone number. We'll generate an 8-digit pairing code — enter it on your phone to link.
                            </p>
                            <div style={{ display: 'flex', gap: '0.6rem' }}>
                                <input
                                    id="whatsapp-phone-input"
                                    type="tel"
                                    value={phoneInput}
                                    onChange={e => setPhoneInput(e.target.value)}
                                    placeholder="e.g. 919876543210"
                                    style={{ flex: 1, padding: '11px 14px', borderRadius: '10px', border: `1.5px solid ${isLightTheme ? '#CBD5E1' : 'rgba(255,255,255,0.12)'}`, fontSize: '0.95rem', outline: 'none', background: isLightTheme ? '#FFFFFF' : 'rgba(15,23,42,0.8)', color: isLightTheme ? '#0F172A' : '#F8FAFC', fontFamily: 'monospace', letterSpacing: '1px' }}
                                    onKeyDown={e => e.key === 'Enter' && handleRequestPairingCode()}
                                />
                                <button
                                    id="btn-get-pairing-code"
                                    onClick={handleRequestPairingCode}
                                    disabled={pairingLoading || !instanceOnline}
                                    style={{
                                        cursor: pairingLoading || !instanceOnline ? 'not-allowed' : 'pointer',
                                        border: 'none', borderRadius: '10px', padding: '11px 18px',
                                        background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                        color: '#FFF', fontWeight: 700, fontSize: '0.87rem',
                                        display: 'flex', alignItems: 'center', gap: '0.4rem',
                                        opacity: pairingLoading || !instanceOnline ? 0.6 : 1,
                                        whiteSpace: 'nowrap',
                                        boxShadow: '0 4px 12px rgba(139,92,246,0.3)',
                                    }}
                                >
                                    {pairingLoading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Shield size={14} />}
                                    {pairingLoading ? 'Generating...' : 'Get Code'}
                                </button>
                            </div>
                            <p style={{ fontSize: '0.78rem', color: isLightTheme ? '#64748B' : '#64748B', marginTop: '0.5rem' }}>
                                Include country code without '+' (India: 91XXXXXXXXXX)
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                STATUS: CONNECTING — Show QR code or pairing code
            ══════════════════════════════════════════════════════════════ */}
            {statusData.status === 'connecting' && (
                <div style={{ background: isLightTheme ? '#FFFFFF' : 'rgba(17, 24, 39, 0.75)', border: `1px solid ${isLightTheme ? '#E2E8F0' : 'rgba(139, 92, 246, 0.3)'}`, borderRadius: '20px', padding: '2.5rem', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', boxShadow: isLightTheme ? '0 10px 30px rgba(0,0,0,0.04)' : '0 12px 36px rgba(0,0,0,0.35)' }}>

                    {/* If we have a pairing code — show it prominently */}
                    {pairingCode ? (
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '60px', height: '60px', borderRadius: '50%', background: isLightTheme ? '#FAF5FF' : 'rgba(139,92,246,0.15)', color: isLightTheme ? '#7C3AED' : '#C084FC', marginBottom: '1.25rem', border: `1px solid ${isLightTheme ? 'rgba(139,92,246,0.3)' : 'rgba(139,92,246,0.3)'}` }}>
                                <Phone size={28} />
                            </div>
                            <h3 style={{ fontSize: '1.35rem', color: isLightTheme ? '#0F172A' : '#FFFFFF', margin: '0 0 0.5rem 0', fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>
                                Enter This Code on Your Phone
                            </h3>
                            <p style={{ color: isLightTheme ? '#475569' : '#94A3B8', fontSize: '0.88rem', maxWidth: '420px', margin: '0 auto 1.5rem auto', lineHeight: 1.6 }}>
                                Open <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>WhatsApp → Linked Devices → Link a Device → Link with phone number instead</strong>, then enter the code below:
                            </p>

                            {/* Pairing Code Display */}
                            <div style={{ display: 'inline-flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {pairingCode.replace(/[^A-Z0-9]/gi, '').split('').map((ch, i) => (
                                    <div
                                        key={i}
                                        style={{
                                            width: '46px', height: '54px', borderRadius: '10px',
                                            background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.9)', border: `2px solid ${isLightTheme ? '#8B5CF6' : 'rgba(139,92,246,0.5)'}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '1.5rem', fontWeight: 900, color: isLightTheme ? '#7C3AED' : '#C084FC',
                                            fontFamily: 'monospace', letterSpacing: 0,
                                            boxShadow: isLightTheme ? '0 2px 8px rgba(139,92,246,0.15)' : '0 2px 10px rgba(139,92,246,0.25)',
                                        }}
                                    >
                                        {ch}
                                    </div>
                                ))}
                            </div>

                            {/* Copy Code Button */}
                            <div style={{ marginBottom: '1.75rem', display: 'flex', justifyContent: 'center' }}>
                                <button
                                    type="button"
                                    onClick={() => handleCopyCode(pairingCode.replace(/[^A-Z0-9]/gi, ''))}
                                    style={{
                                        background: copiedCode ? '#10B981' : (isLightTheme ? '#F1F5F9' : 'rgba(255,255,255,0.08)'),
                                        color: copiedCode ? '#FFFFFF' : (isLightTheme ? '#475569' : '#CBD5E1'),
                                        border: `1px solid ${copiedCode ? '#10B981' : (isLightTheme ? '#CBD5E1' : 'rgba(255,255,255,0.15)')}`,
                                        borderRadius: '8px',
                                        padding: '7px 16px',
                                        fontSize: '0.82rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        transition: 'all 0.2s',
                                    }}
                                >
                                    <Check size={14} /> {copiedCode ? 'Code Copied!' : 'Copy 8-Digit Code'}
                                </button>
                            </div>

                            <p style={{ color: isLightTheme ? '#64748B' : '#94A3B8', fontSize: '0.82rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                                <RefreshCw size={12} style={{ animation: 'spin 3s linear infinite' }} />
                                Waiting for you to enter the code in WhatsApp...
                            </p>

                            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                <button
                                    onClick={handleManualRefresh}
                                    disabled={refreshLoading}
                                    style={{ cursor: refreshLoading ? 'not-allowed' : 'pointer', background: isLightTheme ? '#FFFFFF' : 'rgba(255,255,255,0.05)', border: `1px solid ${isLightTheme ? '#CBD5E1' : 'rgba(255,255,255,0.12)'}`, borderRadius: '10px', padding: '8px 20px', color: isLightTheme ? '#334155' : '#CBD5E1', fontSize: '0.85rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: isLightTheme ? '0 2px 6px rgba(0,0,0,0.02)' : 'none' }}
                                >
                                    <RefreshCw size={13} style={{ animation: refreshLoading ? 'spin 1s linear infinite' : 'none' }} /> Check Status
                                </button>
                                <button
                                    onClick={handleReconnect}
                                    disabled={actionLoading}
                                    style={{
                                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                                        background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                        border: 'none', borderRadius: '10px', padding: '8px 20px',
                                        color: '#FFF', fontSize: '0.83rem', fontWeight: 800,
                                        display: 'flex', alignItems: 'center', gap: '0.4rem',
                                        boxShadow: '0 4px 12px rgba(139,92,246,0.3)',
                                    }}
                                >
                                    <RefreshCw size={13} /> Re-generate Code
                                </button>
                                <button
                                    onClick={handleDisconnect}
                                    disabled={actionLoading}
                                    style={{ cursor: 'pointer', background: isLightTheme ? '#FFFFFF' : 'transparent', border: `1px solid ${isLightTheme ? '#CBD5E1' : 'rgba(255,255,255,0.15)'}`, borderRadius: '10px', padding: '8px 20px', color: isLightTheme ? '#64748B' : '#94A3B8', fontSize: '0.85rem', fontWeight: 700 }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Show QR code panel */
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '2.5rem', alignItems: 'center' }}>
                            {/* QR Code side */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1.5rem', background: isLightTheme ? '#F8FAFC' : 'rgba(15, 23, 42, 0.8)', borderRadius: '16px', border: `1px dashed ${isLightTheme ? 'rgba(139,92,246,0.5)' : 'rgba(139,92,246,0.4)'}` }}>
                                {qrSrc ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <div style={{ width: '230px', height: '230px', background: '#FFFFFF', borderRadius: '12px', padding: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <img
                                                src={qrSrc}
                                                alt="WhatsApp Link QR Code"
                                                style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '6px' }}
                                            />
                                        </div>
                                        <span style={{ fontSize: '0.75rem', color: isLightTheme ? '#64748B' : '#94A3B8', marginTop: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontWeight: 600 }}>
                                            <RefreshCw size={12} style={{ animation: 'spin 4s linear infinite' }} />
                                            Auto-refreshing every 2.5 s...
                                        </span>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '230px', width: '230px', gap: '1rem', color: isLightTheme ? '#7C3AED' : '#C084FC' }}>
                                        <Loader2 size={36} style={{ animation: 'spin 1s linear infinite' }} />
                                        <p style={{ fontSize: '0.82rem', fontWeight: 600, margin: 0, textAlign: 'center', color: isLightTheme ? '#64748B' : '#94A3B8' }}>
                                            Generating QR Code<br />from Evolution API...
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Instructions side */}
                            <div>
                                <h3 style={{ fontSize: '1.3rem', color: isLightTheme ? '#0F172A' : '#FFFFFF', margin: '0 0 1rem 0', fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>
                                    Scan with WhatsApp
                                </h3>
                                <ol style={{ paddingLeft: '1.25rem', color: isLightTheme ? '#475569' : '#94A3B8', fontSize: '0.9rem', lineHeight: 1.9, margin: '0 0 1.75rem 0' }}>
                                    <li>Open <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>WhatsApp</strong> on your phone.</li>
                                    <li>Tap <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>Menu</strong> (Android) or <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>Settings</strong> (iOS).</li>
                                    <li>Select <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>Linked Devices</strong>.</li>
                                    <li>Tap <strong style={{ color: isLightTheme ? '#0F172A' : '#F8FAFC' }}>Link a Device</strong>.</li>
                                    <li>Point your camera at the QR code.</li>
                                </ol>

                                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                    <button
                                        onClick={handleManualRefresh}
                                        disabled={refreshLoading}
                                        style={{ cursor: refreshLoading ? 'not-allowed' : 'pointer', background: isLightTheme ? '#FFFFFF' : 'rgba(255,255,255,0.05)', border: `1px solid ${isLightTheme ? '#CBD5E1' : 'rgba(255,255,255,0.12)'}`, borderRadius: '10px', padding: '8px 18px', color: isLightTheme ? '#334155' : '#CBD5E1', fontSize: '0.83rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem', boxShadow: isLightTheme ? '0 2px 6px rgba(0,0,0,0.02)' : 'none' }}
                                    >
                                        <RefreshCw size={13} style={{ animation: refreshLoading ? 'spin 1s linear infinite' : 'none' }} /> Check Status
                                    </button>
                                    <button
                                        onClick={handleReconnect}
                                        disabled={actionLoading}
                                        style={{
                                            cursor: actionLoading ? 'not-allowed' : 'pointer',
                                            background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                                            border: 'none', borderRadius: '10px', padding: '8px 20px',
                                            color: '#FFF', fontSize: '0.83rem', fontWeight: 800,
                                            display: 'flex', alignItems: 'center', gap: '0.4rem',
                                            opacity: actionLoading ? 0.6 : 1,
                                            boxShadow: '0 4px 12px rgba(139,92,246,0.3)',
                                        }}
                                    >
                                        <RefreshCw size={13} /> Re-generate QR
                                    </button>
                                    <button
                                        onClick={handleDisconnect}
                                        disabled={actionLoading}
                                        style={{ cursor: 'pointer', background: isLightTheme ? '#FFFFFF' : 'transparent', border: `1px solid ${isLightTheme ? '#CBD5E1' : 'rgba(255,255,255,0.15)'}`, borderRadius: '10px', padding: '8px 18px', color: isLightTheme ? '#64748B' : '#94A3B8', fontSize: '0.83rem', fontWeight: 700 }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── Info Footer ────────────────────────────────────────────── */}
            <div style={{ marginTop: '1.5rem', padding: '1.25rem 1.5rem', background: isLightTheme ? '#FAF5FF' : 'rgba(139,92,246,0.08)', borderRadius: '14px', border: `1px solid ${isLightTheme ? 'rgba(139,92,246,0.25)' : 'rgba(139,92,246,0.2)'}`, display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <Shield size={18} style={{ color: isLightTheme ? '#7C3AED' : '#C084FC', flexShrink: 0, marginTop: '2px' }} />
                <div>
                    <p style={{ fontSize: '0.85rem', fontWeight: 700, color: isLightTheme ? '#5B21B6' : '#F8FAFC', margin: '0 0 0.25rem 0' }}>Privacy &amp; Security</p>
                    <p style={{ fontSize: '0.82rem', color: isLightTheme ? '#6D28D9' : '#94A3B8', margin: 0, lineHeight: 1.6 }}>
                        Messages are sent via your linked WhatsApp account through a self-hosted Evolution API server. No message content is stored by third-party services. You can disconnect at any time.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default InviteWhatsAppSettings;
